import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from '~/components/button';
import { ErrorMessage } from '~/components/error-message';
import { type ImageType, ImageSelector, GLOBE_HOME } from '~/components/image';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { Separator } from '~/components/separator';
import { Description, Text, Title } from '~/components/typography';
import { useCopy } from '~/hooks';
import { UtilityAccount as ExistingUtilityAccount } from '~/services/fetch-existing-utility-accounts';
import { isPerch } from '~/utils/constants';
import { StyledRadioGroup } from './utility-account-selection.style';

export const UtilityAccountList = styled.ul`
  list-style-type: disc;
  padding: 8px 0 0 16px;
`;

export const UtilityAccountListItem = styled.li`
  margin-bottom: 16px;
`;

const InfoContainer = styled.div`
  padding: 8px 0;
`;

export const UtilityAccountSelection = ({
  context,
  error,
  flags,
  next,
  onConnectNewUtility,
  overrideCopy,
}: UtilityAccountSelectionProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const { utilityAccounts, billingAddress } = context;
  const [selectedId, setSelectedId] = useState(utilityAccounts[0]?.id);
  const [limit, setLimit] = useState(4);

  useEffect(() => {
    setLimit(4);
  }, [utilityAccounts.length]);
  return (
    <TwoColumnGrid testId="screen-UtilityAccountSelection">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={5}>{copy.description}</Description>
        {utilityAccounts.length > 1 && !isPerch() ? (
          <Container>
            <Text mb={4} textStyle="heading500">
              Select utility account
            </Text>
            <StyledRadioGroup
              name="utilityAccountId"
              options={utilityAccounts.slice(0, limit).map(ua => ({
                id: ua.id,
                children: (
                  <Container key={ua.id} onClick={() => setSelectedId(ua.id)}>
                    <Text>{formatUtilityAccountDetails(ua)}</Text>
                  </Container>
                ),
                value: ua.id.toString(),
              }))}
              highlightSelected
              isChecked={(_, value) => value === selectedId.toString()}
              onChange={value => setSelectedId(Number(value))}
              width="initial"
            />
            {utilityAccounts.length > 4 && limit <= 4 && (
              <Container mt={4}>
                <Link
                  color="primary"
                  openInNewTab={false}
                  onClick={() => setLimit(utilityAccounts.length)}
                >
                  See all
                </Link>
              </Container>
            )}
          </Container>
        ) : (
          <Container>
            <Text mb={2} textStyle="heading500">
              Your utility account information
            </Text>
            <Text>
              <strong>Utility :</strong> {utilityAccounts[0].utility.displayName}
            </Text>
            {!!billingAddress && !!billingAddress.streetOne && (
              <InfoContainer>
                <strong>Mailing address :</strong> {billingAddress.streetOne}, {billingAddress.city}
                , {billingAddress.state} {billingAddress.zipCode}
              </InfoContainer>
            )}
            {utilityAccounts.some(hasDisplayableUtilityAccountDetails) ? (
              <UtilityAccountList>
                {utilityAccounts.filter(hasDisplayableUtilityAccountDetails).map(ua => (
                  <UtilityAccountListItem key={ua.id}>
                    <Text>{formatPerchFunnelUtilityAccountDetails(ua)}</Text>
                  </UtilityAccountListItem>
                ))}
              </UtilityAccountList>
            ) : null}
          </Container>
        )}
        <Container mt={7}>
          <Button
            mode="primary"
            type="submit"
            testId="continue-with-utility-button"
            onClick={() => next({ selectedUtilityAccountId: selectedId })}
          >
            Continue
          </Button>
          <ErrorMessage mt={2} error={error} />
          {!!flags?.showNewUtilityButton && (
            <>
              <Separator innerText="or" />
              <Button
                testId="connect-new-utility-button"
                mode="default"
                onClick={() => onConnectNewUtility()}
              >
                {copy.connectNewUtilityButton}
              </Button>
            </>
          )}
        </Container>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary centered>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const hasDisplayableUtilityAccountDetails = (
  utilityAccount: ExistingUtilityAccount,
): boolean =>
  !utilityAccount.hasInValidScraperDetails &&
  (!!utilityAccount.displayServiceAddress || !!utilityAccount.accountNumber);

export const defaultCopy = (context: UtilityAccountSelectionContext) => {
  const titleAndDescriptionCopy = (context: UtilityAccountSelectionContext) => {
    const hasOneAccount = context.utilityAccounts.length === 1;
    const primaryAccount = context.utilityAccounts[0];
    const noSyncedAccounts =
      hasOneAccount && (!primaryAccount.accountNumber || primaryAccount.hasInValidScraperDetails);

    if (noSyncedAccounts) {
      return {
        title: 'Welcome back! Looks like you’ve started signing up in the past.',
        description: 'We’ve saved the information you shared previously.',
      };
    } else if (hasOneAccount) {
      return {
        title: 'Welcome back! Looks like you’ve synced a utility account in the past.',
        description: 'We’ve saved the utility account information you shared previously.',
      };
    }
    return {
      title: 'Welcome back! Looks like you’ve synced utility accounts in the past.',
      description: 'Please select your preferred utility account from the options listed below.',
    };
  };

  return {
    title: titleAndDescriptionCopy(context).title,
    description: titleAndDescriptionCopy(context).description,
    imageType: GLOBE_HOME as ImageType,
    connectNewUtilityButton: 'Connect new utility',
  };
};

export const formatPerchFunnelUtilityAccountDetails = (
  utilityAccount: ExistingUtilityAccount,
): any => {
  if (!hasDisplayableUtilityAccountDetails(utilityAccount)) {
    return null;
  }
  const serviceAddress = utilityAccount.displayServiceAddress || '';
  const accountNumber = utilityAccount.accountNumber || '';
  return (
    <>
      {!!serviceAddress && (
        <InfoContainer>
          <strong>Service address :</strong> {serviceAddress}
        </InfoContainer>
      )}
      {!!accountNumber && (
        <InfoContainer>
          <strong>Account number :</strong> {accountNumber}
        </InfoContainer>
      )}
    </>
  );
};

export const formatUtilityAccountDetails = (utilityAccount: ExistingUtilityAccount): string => {
  let utilityName = utilityAccount.utility.displayName;
  if (utilityAccount.hasInValidScraperDetails) {
    return `${utilityName} Account`;
  }
  let { accountNumber, displayServiceAddress } = utilityAccount;
  utilityName = !accountNumber && !displayServiceAddress ? `${utilityName} Account` : utilityName;
  displayServiceAddress = displayServiceAddress ? `. ${displayServiceAddress}` : '';
  accountNumber = accountNumber ? `. #${accountNumber}` : '';

  return `${utilityName}${displayServiceAddress}${accountNumber}`;
};

export type UtilityAccountSelectionCopy = ReturnType<typeof defaultCopy>;

export interface UtilityAccountSelectionProps {
  context: UtilityAccountSelectionContext;
  error?: string;
  flags?: {
    showNewUtilityButton?: boolean;
  };
  next: (payload: UtilityAccountSelectionForm) => void;
  onConnectNewUtility: () => void;
  overrideCopy?: (context: UtilityAccountSelectionContext) => Partial<UtilityAccountSelectionCopy>;
}

export interface UtilityAccountSelectionForm {
  selectedUtilityAccountId: number;
}

export interface UtilityAccountSelectionContext {
  utilityAccounts: ExistingUtilityAccount[];
  billingAddress?: {
    streetOne: string;
    city: string;
    state: string;
    zipCode: string;
    streetTwo?: string;
  };
}
